import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';

import Modal from '../../components/Modal';
import { UnifiedTable } from '../../components/UnifiedTable';
import { ApiContext } from '../../contexts/ApiContext';

const PAGE_SIZES = [25, 50, 100, 250, 500, 1000];

function normalizeColumn(col, idx) {
    return {
        key: col?.key || `col_${idx + 1}`,
        header: col?.header || col?.key || `Column ${idx + 1}`,
        type: col?.type || 'text',
        sticky: Boolean(col?.sticky),
        hidden: Boolean(col?.hidden),
        width: typeof col?.width === 'number' ? col.width : undefined,
    };
}

function ProjectTableDetail({ workspaceId, tableId, canEdit, onColumnsChanged }) {
    const { request, authFetch, API_BASE_URL, showStatus } = useContext(ApiContext);

    const base = `/api/v1/workspaces/${workspaceId}/project-tables/${tableId}`;

    const [loading, setLoading] = useState(false);
    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [search, setSearch] = useState('');
    const [sortKey, setSortKey] = useState('');
    const [sortDir, setSortDir] = useState('asc');

    const [selectedRows, setSelectedRows] = useState([]);

    const [rowModalOpen, setRowModalOpen] = useState(false);
    const [editingRowId, setEditingRowId] = useState(null);
    const [editingRowData, setEditingRowData] = useState({});

    const [schemaModalOpen, setSchemaModalOpen] = useState(false);
    const [schemaDraft, setSchemaDraft] = useState([]);

    const [batchModalOpen, setBatchModalOpen] = useState(false);
    const [batchPatchState, setBatchPatchState] = useState({});

    const fileInputRef = useRef(null);

    const offset = (page - 1) * pageSize;

    const loadTable = useCallback(async () => {
        if (!workspaceId || !tableId) return;
        setLoading(true);
        try {
            const q = new URLSearchParams();
            q.set('limit', String(pageSize));
            q.set('offset', String(offset));
            if (search.trim()) q.set('search', search.trim());
            if (sortKey) {
                q.set('sort_by', sortKey);
                q.set('sort_dir', sortDir);
            }

            const data = await request(`${base}/rows?${q.toString()}`);
            const normalizedColumns = (data.columns || []).map(normalizeColumn);

            setColumns(normalizedColumns);
            setRows(data.rows || []);
            setTotal(Number(data.total || 0));
            setSelectedRows([]);
            onColumnsChanged?.(normalizedColumns);
        } catch (error) {
            showStatus('error', error.message || 'Не удалось загрузить таблицу.');
        } finally {
            setLoading(false);
        }
    }, [workspaceId, tableId, pageSize, offset, search, sortKey, sortDir, request, showStatus, base, onColumnsChanged]);

    useEffect(() => {
        loadTable();
    }, [loadTable]);

    useEffect(() => {
        setPage(1);
        setSelectedRows([]);
    }, [tableId]);

    const tableColumns = useMemo(() => {
        const indexCol = {
            key: 'rowNum',
            header: '№',
            type: 'number',
            width: 56,
            sticky: true,
            sortable: false,
            resizable: false,
            exportable: false,
        };
        return [indexCol, ...columns];
    }, [columns]);

    const tableData = useMemo(
        () => rows.map((row, idx) => ({
            id: row.id,
            row_order: row.row_order,
            rowNum: offset + idx + 1,
            ...(row.data || {}),
        })),
        [rows, offset]
    );

    const openCreateRowModal = useCallback(() => {
        const next = {};
        columns.forEach((col) => {
            next[col.key] = '';
        });
        setEditingRowId(null);
        setEditingRowData(next);
        setRowModalOpen(true);
    }, [columns]);

    const openEditRowModal = useCallback((row) => {
        if (!canEdit) return;
        const next = {};
        columns.forEach((col) => {
            next[col.key] = row[col.key] ?? '';
        });
        setEditingRowId(row.id);
        setEditingRowData(next);
        setRowModalOpen(true);
    }, [columns, canEdit]);

    const saveRow = useCallback(async () => {
        try {
            if (editingRowId) {
                await request(`${base}/rows/${editingRowId}`, {
                    method: 'PATCH',
                    body: JSON.stringify({ data: editingRowData }),
                });
                showStatus('success', 'Строка обновлена.');
            } else {
                await request(`${base}/rows`, {
                    method: 'POST',
                    body: JSON.stringify({ data: editingRowData }),
                });
                showStatus('success', 'Строка добавлена.');
            }
            setRowModalOpen(false);
            await loadTable();
        } catch (error) {
            showStatus('error', error.message || 'Не удалось сохранить строку.');
        }
    }, [editingRowId, editingRowData, request, base, showStatus, loadTable]);

    const duplicateRow = useCallback(async () => {
        if (!editingRowId) return;
        try {
            await request(`${base}/rows/${editingRowId}/duplicate`, { method: 'POST' });
            showStatus('success', 'Строка продублирована.');
            setRowModalOpen(false);
            await loadTable();
        } catch (error) {
            showStatus('error', error.message || 'Не удалось продублировать строку.');
        }
    }, [editingRowId, request, base, showStatus, loadTable]);

    const deleteRow = useCallback(async () => {
        if (!editingRowId) return;
        if (!window.confirm('Удалить эту строку?')) return;
        try {
            await request(`${base}/rows/${editingRowId}`, { method: 'DELETE' });
            showStatus('success', 'Строка удалена.');
            setRowModalOpen(false);
            await loadTable();
        } catch (error) {
            showStatus('error', error.message || 'Не удалось удалить строку.');
        }
    }, [editingRowId, request, base, showStatus, loadTable]);

    const openSchemaEditor = useCallback(() => {
        setSchemaDraft(columns.map((col) => ({ ...col })));
        setSchemaModalOpen(true);
    }, [columns]);

    const addSchemaColumn = useCallback(() => {
        setSchemaDraft((prev) => ([
            ...prev,
            {
                key: `col_${prev.length + 1}`,
                header: `Колонка ${prev.length + 1}`,
                type: 'text',
                sticky: false,
                hidden: false,
                width: 160,
            },
        ]));
    }, []);

    const saveSchema = useCallback(async () => {
        try {
            await request(base, {
                method: 'PATCH',
                body: JSON.stringify({ columns: schemaDraft }),
            });
            showStatus('success', 'Колонки сохранены.');
            setSchemaModalOpen(false);
            setPage(1);
            await loadTable();
        } catch (error) {
            showStatus('error', error.message || 'Не удалось сохранить колонки.');
        }
    }, [request, base, schemaDraft, showStatus, loadTable]);

    const onClickUploadExcel = useCallback(() => {
        if (!fileInputRef.current) return;
        fileInputRef.current.value = '';
        fileInputRef.current.click();
    }, []);

    const onExcelSelected = useCallback(async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setLoading(true);
        try {
            await authFetch(`${API_BASE_URL}${base}/upload-csv`, {
                method: 'POST',
                body: formData,
            });
            showStatus('success', `Файл загружен: ${file.name}`);
            setPage(1);
            await loadTable();
        } catch (error) {
            showStatus('error', error.message || 'Не удалось загрузить файл.');
        } finally {
            setLoading(false);
        }
    }, [authFetch, API_BASE_URL, base, showStatus, loadTable]);

    const downloadExcel = useCallback(async () => {
        setLoading(true);
        try {
            let cols = columns;
            let allRows = [];
            let offset0 = 0;
            const limit = 1000;
            let totalCount = Infinity;
            while (offset0 < totalCount) {
                const data = await request(`${base}/rows?limit=${limit}&offset=${offset0}`);
                cols = (data.columns || []).map(normalizeColumn);
                allRows = allRows.concat(data.rows || []);
                totalCount = Number(data.total || 0);
                if (!data.rows || data.rows.length === 0) break;
                offset0 += limit;
            }

            const headers = cols.map((col) => col.header);
            const sheetRows = allRows.map((row) => {
                const obj = {};
                cols.forEach((col) => {
                    obj[col.header] = row.data?.[col.key] ?? '';
                });
                return obj;
            });

            const ws = XLSX.utils.json_to_sheet(sheetRows, { header: headers });
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Таблица');
            XLSX.writeFile(wb, `project-table-${tableId}.xlsx`);
        } catch (error) {
            showStatus('error', error.message || 'Не удалось скачать таблицу.');
        } finally {
            setLoading(false);
        }
    }, [request, base, columns, tableId, showStatus]);

    const openBatchUpdateModal = useCallback(() => {
        const initial = {};
        columns.forEach((col) => {
            initial[col.key] = { enabled: false, value: '' };
        });
        setBatchPatchState(initial);
        setBatchModalOpen(true);
    }, [columns]);

    const applyBatchUpdate = useCallback(async () => {
        const patch = {};
        Object.entries(batchPatchState).forEach(([key, cfg]) => {
            if (cfg?.enabled) {
                patch[key] = cfg.value ?? '';
            }
        });

        if (!Object.keys(patch).length) {
            showStatus('error', 'Выбери хотя бы одно поле для пакетного обновления.');
            return;
        }

        try {
            const data = await request(`${base}/rows/batch-update`, {
                method: 'POST',
                body: JSON.stringify({ row_ids: selectedRows, patch }),
            });
            showStatus('success', `Обновлено строк: ${data.affected || 0}`);
            setBatchModalOpen(false);
            await loadTable();
        } catch (error) {
            showStatus('error', error.message || 'Не удалось выполнить пакетное обновление.');
        }
    }, [batchPatchState, request, base, selectedRows, showStatus, loadTable]);

    const applyBatchDelete = useCallback(async () => {
        if (!selectedRows.length) return;
        if (!window.confirm(`Удалить выбранные строки (${selectedRows.length})?`)) return;

        try {
            const data = await request(`${base}/rows/batch-delete`, {
                method: 'POST',
                body: JSON.stringify({ row_ids: selectedRows }),
            });
            showStatus('success', `Удалено строк: ${data.affected || 0}`);
            await loadTable();
        } catch (error) {
            showStatus('error', error.message || 'Не удалось удалить выбранные строки.');
        }
    }, [request, base, selectedRows, showStatus, loadTable]);

    const toolbarActions = useMemo(() => {
        const actions = [];
        if (canEdit) {
            actions.push({ label: 'Загрузить Excel', onClick: onClickUploadExcel, disabled: loading });
        }
        actions.push({ label: 'Скачать Excel', onClick: downloadExcel, disabled: loading });
        if (canEdit) {
            actions.push({ label: 'Добавить строку', onClick: openCreateRowModal, disabled: loading });
            actions.push({ label: 'Редактировать колонки', onClick: openSchemaEditor, disabled: loading });
        }
        return actions;
    }, [canEdit, onClickUploadExcel, downloadExcel, openCreateRowModal, openSchemaEditor, loading]);

    const bulkActions = useMemo(() => {
        if (!canEdit) return [];
        return [
            { label: 'Пакетное обновление', onClick: openBatchUpdateModal },
            { label: 'Удалить выбранное', onClick: applyBatchDelete },
        ];
    }, [canEdit, openBatchUpdateModal, applyBatchDelete]);

    return (
        <div className="project-assortment">
            <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                style={{ display: 'none' }}
                onChange={onExcelSelected}
            />

            <UnifiedTable
                tableId={`project-table-${tableId}`}
                columns={tableColumns}
                data={tableData}
                totalRows={total}
                page={page}
                pageSize={pageSize}
                pageSizes={PAGE_SIZES}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                sortKey={sortKey}
                sortDir={sortDir}
                onSortChange={(key, dir) => {
                    if (key === 'rowNum') return;
                    setSortKey(key);
                    setSortDir(dir);
                }}
                serverSort={true}
                search={search}
                onSearchChange={(value) => {
                    setSearch(value);
                    setPage(1);
                }}
                selectable={canEdit}
                selectedRows={selectedRows}
                onSelectionChange={setSelectedRows}
                bulkActions={bulkActions}
                toolbarActions={toolbarActions}
                exportFilename={`project-table-${tableId}`}
                onExportCsv={false}
                loading={loading}
                onRowClick={openEditRowModal}
                getRowKey={(row) => row.id || `${row.row_order}`}
            />

            <Modal
                open={rowModalOpen}
                onClose={() => setRowModalOpen(false)}
                title={editingRowId ? 'Редактирование строки' : 'Новая строка'}
            >
                <div className="project-assortment__form">
                    {columns.map((col) => (
                        <label key={col.key}>
                            <span>{col.header}</span>
                            <input
                                type="text"
                                value={editingRowData[col.key] ?? ''}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setEditingRowData((prev) => ({ ...prev, [col.key]: value }));
                                }}
                            />
                        </label>
                    ))}
                    <div className="project-assortment__actions">
                        <button type="button" className="primary" onClick={saveRow}>Сохранить</button>
                        {editingRowId && (
                            <>
                                <button type="button" className="ghost" onClick={duplicateRow}>Дублировать</button>
                                <button type="button" className="ghost" onClick={deleteRow}>Удалить</button>
                            </>
                        )}
                    </div>
                </div>
            </Modal>

            <Modal
                open={schemaModalOpen}
                onClose={() => setSchemaModalOpen(false)}
                title="Редактирование колонок"
                wide
                panelStyle={{ width: 'min(96vw, 800px)', maxWidth: '800px' }}
            >
                <div className="project-assortment__schema">
                    {schemaDraft.map((col, idx) => (
                        <div key={`${col.key}-${idx}`} className="project-assortment__schema-row">
                            <input
                                type="text"
                                placeholder="Ключ"
                                value={col.key}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setSchemaDraft((prev) => prev.map((item, i) => (i === idx ? { ...item, key: value } : item)));
                                }}
                            />
                            <input
                                type="text"
                                placeholder="Название"
                                value={col.header}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setSchemaDraft((prev) => prev.map((item, i) => (i === idx ? { ...item, header: value } : item)));
                                }}
                            />
                            <select
                                value={col.type || 'text'}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setSchemaDraft((prev) => prev.map((item, i) => (i === idx ? { ...item, type: value } : item)));
                                }}
                            >
                                <option value="text">text</option>
                                <option value="number">number</option>
                                <option value="image">image</option>
                                <option value="url">url</option>
                            </select>
                            <label className="project-assortment__schema-check">
                                <input
                                    type="checkbox"
                                    checked={Boolean(col.sticky)}
                                    onChange={(event) => {
                                        const checked = event.target.checked;
                                        setSchemaDraft((prev) => prev.map((item, i) => (i === idx ? { ...item, sticky: checked } : item)));
                                    }}
                                />
                                Закрепить
                            </label>
                            <label className="project-assortment__schema-check">
                                <input
                                    type="checkbox"
                                    checked={Boolean(col.hidden)}
                                    onChange={(event) => {
                                        const checked = event.target.checked;
                                        setSchemaDraft((prev) => prev.map((item, i) => (i === idx ? { ...item, hidden: checked } : item)));
                                    }}
                                />
                                Скрыть
                            </label>
                            <button
                                type="button"
                                className="ghost"
                                onClick={() => {
                                    setSchemaDraft((prev) => prev.filter((_, i) => i !== idx));
                                }}
                            >
                                Удалить
                            </button>
                        </div>
                    ))}

                    <div className="project-assortment__actions">
                        <button type="button" className="ghost" onClick={addSchemaColumn}>Добавить колонку</button>
                        <button type="button" className="primary" onClick={saveSchema}>Сохранить колонки</button>
                    </div>
                </div>
            </Modal>

            <Modal
                open={batchModalOpen}
                onClose={() => setBatchModalOpen(false)}
                title={`Пакетное обновление (${selectedRows.length} строк)`}
                wide
            >
                <div className="project-assortment__schema">
                    {columns.map((col) => {
                        const state = batchPatchState[col.key] || { enabled: false, value: '' };
                        return (
                            <div key={col.key} className="project-assortment__batch-row">
                                <label className="project-assortment__schema-check">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(state.enabled)}
                                        onChange={(event) => {
                                            const checked = event.target.checked;
                                            setBatchPatchState((prev) => ({
                                                ...prev,
                                                [col.key]: { ...state, enabled: checked },
                                            }));
                                        }}
                                    />
                                    {col.header}
                                </label>
                                <input
                                    type="text"
                                    value={state.value ?? ''}
                                    disabled={!state.enabled}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setBatchPatchState((prev) => ({
                                            ...prev,
                                            [col.key]: { ...state, value },
                                        }));
                                    }}
                                />
                            </div>
                        );
                    })}
                    <div className="project-assortment__actions">
                        <button type="button" className="primary" onClick={applyBatchUpdate}>Применить пакетное обновление</button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default ProjectTableDetail;
